import { useEffect, useState } from 'react'
import { apiClient } from '@/api/client'
import { useAuth } from '@/contexts/AuthContext'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { LogoImage } from '@/utils/LogoImage'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { formatApiError } from '@/utils/httpErrors'

interface RegistrationFormData {
  firm_name: string
  brand_key: string
  logo_url: string
  primary_color: string
  secondary_color: string
  custom_domain: string
}

const INITIAL_COLORS = { primary_color: '#1d4ed8', secondary_color: '#0f172a' }

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function BrandRegistrationPage() {
  const { user } = useAuth()
  const [form, setForm] = useState<RegistrationFormData>({
    firm_name: user?.firm ?? '',
    brand_key: slugify(user?.firm ?? ''),
    logo_url: '',
    ...INITIAL_COLORS,
    custom_domain: '',
  })
  const [keyEdited, setKeyEdited] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [alreadyApproved, setAlreadyApproved] = useState(false)
  const [checking, setChecking] = useState(true)
  // PLAT-SIGNIN-POLISH-1: the preview shows Google only where it is set up.
  const [googleEnabled, setGoogleEnabled] = useState(false)

  useEffect(() => {
    apiClient.get<{ has_approved_brand: boolean }>('/v1/brands/my-registration-status')
      .then(res => setAlreadyApproved(res.data.has_approved_brand))
      .catch(() => { /* backend unavailable — allow the form, submit will re-validate */ })
      .finally(() => setChecking(false))
  }, [])

  useEffect(() => {
    apiClient.get<{ google: boolean }>('/auth/sso-config')
      .then(res => setGoogleEnabled(res?.data?.google === true))
      .catch(() => setGoogleEnabled(false))
  }, [])

  const update = (field: keyof RegistrationFormData, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }))

  function handleFirmNameChange(value: string) {
    update('firm_name', value)
    if (!keyEdited) update('brand_key', slugify(value))
  }

  async function handleLogoUpload(file: File) {
    setUploading(true)
    setError(null)
    try {
      const body = new FormData()
      body.append('file', file)
      const res = await apiClient.post<{ logo_url: string }>('/v1/documents/upload-logo', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      update('logo_url', res.data.logo_url)
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setUploading(false)
    }
  }

  async function handleSubmit() {
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.post('/v1/brands/register', form)
      setSubmitted(true)
    } catch (err: unknown) {
      setError(formatApiError(err))
    } finally {
      setSubmitting(false)
    }
  }

  if (checking) {
    return <div className="p-6 text-sm text-gray-500">Loading…</div>
  }

  if (alreadyApproved) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Alert>
          <AlertDescription>
            Your firm already has an approved custom brand live on the platform.
            Contact your Yansar representative if you need to change it.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="shadow-sm">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-xl font-semibold mb-2">Registration Submitted</h2>
            <p className="text-gray-500 text-sm">
              Your white-label brand request for <strong>{form.firm_name}</strong> has been
              submitted for review. A super admin will approve or reject it shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const canSubmit = form.firm_name.trim().length > 0 && form.brand_key.trim().length > 0

  return (
    <div className="p-6 max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Register a White-Label Brand</CardTitle>
          <CardDescription>
            Request a custom-branded portal for your firm. A super admin reviews every
            request before it goes live.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Firm Name *</Label>
            <Input
              value={form.firm_name}
              onChange={e => handleFirmNameChange(e.target.value)}
              placeholder="Acme CPA LLC"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Brand Key *</Label>
            <Input
              value={form.brand_key}
              onChange={e => { setKeyEdited(true); update('brand_key', slugify(e.target.value)) }}
              placeholder="acme-cpa"
            />
            <p className="text-xs text-gray-400">
              Used in the URL: <code>?brand={form.brand_key || '…'}</code>. Lowercase letters,
              numbers, and hyphens only.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Logo</Label>
            <Input
              type="file"
              accept="image/png,image/jpeg"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f) }}
              disabled={uploading}
            />
            {uploading && <p className="text-xs text-gray-400">Uploading…</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={e => update('primary_color', e.target.value)}
                  className="h-9 w-12 rounded border border-gray-200 cursor-pointer"
                />
                <Input
                  value={form.primary_color}
                  onChange={e => update('primary_color', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Secondary Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.secondary_color}
                  onChange={e => update('secondary_color', e.target.value)}
                  className="h-9 w-12 rounded border border-gray-200 cursor-pointer"
                />
                <Input
                  value={form.secondary_color}
                  onChange={e => update('secondary_color', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Custom Domain (optional)</Label>
            <Input
              value={form.custom_domain}
              onChange={e => update('custom_domain', e.target.value)}
              placeholder="demo.acmecpa.com"
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            className="w-full"
            style={{ backgroundColor: form.primary_color }}
            disabled={!canSubmit || submitting || uploading}
            onClick={handleSubmit}
          >
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </Button>
        </CardContent>
      </Card>

      <Card className="shadow-sm h-fit">
        <CardHeader>
          <CardTitle className="text-sm text-gray-500">Live Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="rounded-lg border border-gray-200 p-6 flex flex-col items-center text-center"
            style={{ backgroundColor: '#fff' }}
          >
            {form.logo_url ? (
              <LogoImage
                src={form.logo_url}
                name={form.firm_name || 'Your Firm Name'}
                className="h-10 mb-4 object-contain"
                fallbackClassName="h-10 mb-4 flex items-center text-sm font-semibold"
              />
            ) : (
              <div className="h-10 mb-4 flex items-center text-gray-300 text-xs">No logo uploaded</div>
            )}
            <h3 className="font-semibold mb-1" style={{ color: form.secondary_color }}>
              {form.firm_name || 'Your Firm Name'}
            </h3>
            <p className="text-xs text-gray-400 mb-4">Sign in to your portal</p>
            {googleEnabled && (
              <div
                className="w-full rounded-md py-2 text-sm font-medium text-white"
                style={{ backgroundColor: form.primary_color }}
              >
                Sign in with Google
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
